<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, ConfirmAction, EmptyState, FormField, Modal, Section, Select, Table, Textarea, Toolbar } from '@drama/ui'
import { listNovelReviews, getNovelReview, prepareNovelReview, decideNovelReview } from '../../../api/novel/reviews.js'
import { publishNovelPack } from '../../../api/novel/packs.js'
import { useNovelPageText } from '../novel-page-text.js'

const emit = defineEmits(['published'])
const { t } = useI18n()
const { apiErrorLine } = useNovelPageText()
const tr = (key, params) => t(`novelReview.${key}`, params ?? {})
const status = ref('pending')
const items = ref([])
const loading = ref(false)
const busy = ref(false)
const detail = ref(null)
const error = ref('')
const reason = ref('')
const opened = ref(false)
const options = computed(() => ['pending', 'approved', 'rejected', 'withdrawn'].map(value => ({ value, label: tr(`status.${value}`) })))
const columns = computed(() => [
  { key: 'title', label: tr('submission'), width: '240px' },
  { key: 'source_revision', label: tr('revision'), width: '90px' },
  { key: 'status', label: tr('reviewState'), width: '120px' },
  { key: 'preparation_status', label: tr('preparation'), width: '130px' },
  { key: 'actions', label: tr('actions'), width: '120px', align: 'right' },
])
onMounted(load)
async function load() {
  loading.value = true
  error.value = ''
  try { items.value = (await listNovelReviews(status.value)).items }
  catch (failure) { error.value = apiErrorLine(failure) }
  finally { loading.value = false }
}
async function inspect(review) {
  busy.value = true
  error.value = ''
  reason.value = ''
  try {
    detail.value = await getNovelReview(review.id)
    opened.value = true
  } catch (failure) { error.value = apiErrorLine(failure) }
  finally { busy.value = false }
}
async function prepare(providers = []) {
  busy.value = true
  error.value = ''
  try {
    await prepareNovelReview(detail.value.review.id, providers)
    detail.value = await getNovelReview(detail.value.review.id)
    await load()
  } catch (failure) { error.value = apiErrorLine(failure) }
  finally { busy.value = false }
}
async function decide(decision) {
  busy.value = true
  error.value = ''
  try {
    await decideNovelReview(detail.value.review.id, { decision, reason: reason.value,
      snapshot_hash: detail.value.review.snapshot_hash })
    detail.value = await getNovelReview(detail.value.review.id)
    await load()
  } catch (failure) {
    error.value = apiErrorLine(failure)
    throw new Error(error.value, { cause: failure })
  } finally { busy.value = false }
}
async function prepareDrama() {
  await prepare(['vidu', 'vivix'])
  if (error.value) throw new Error(error.value)
}
async function publish() {
  busy.value = true
  try {
    await publishNovelPack(detail.value.review.pack_id, detail.value.review.id)
    opened.value = false
    emit('published')
    await load()
  } catch (failure) { throw new Error(apiErrorLine(failure), { cause: failure }) }
  finally { busy.value = false }
}
const formatted = value => JSON.stringify(value, null, 2)
</script>

<template>
  <Section
    :title="tr('title')"
    data-testid="novel-review-queue"
  >
    <p>{{ tr('description') }}</p>
    <Toolbar>
      <Select
        v-model="status"
        :options="options"
        :aria-label="tr('reviewState')"
        @update:model-value="load"
      />
      <template #actions>
        <Button
          :loading="loading"
          variant="secondary"
          @click="load"
        >
          {{ tr('refresh') }}
        </Button>
      </template>
    </Toolbar>
    <p
      v-if="error"
      class="review-error"
      role="alert"
    >
      {{ error }}
    </p>
    <EmptyState
      v-if="!loading && !items.length"
      :title="tr('empty')"
    />
    <Table
      v-else
      :rows="items"
      :columns="columns"
    >
      <template #title="{ row }">
        <strong>{{ row.title }}</strong><br><code>{{ row.id }}</code>
      </template>
      <template #status="{ row }">
        <Badge>{{ tr(`status.${row.status}`) }}</Badge>
      </template>
      <template #preparation_status="{ row }">
        {{ tr(`preparationStatus.${row.preparation_status}`) }}
      </template>
      <template #actions="{ row }">
        <Button
          size="sm"
          :disabled="busy"
          :data-testid="`novel-review-inspect-${row.id}`"
          @click="inspect(row)"
        >
          {{ tr('inspect') }}
        </Button>
      </template>
    </Table>
  </Section>
  <Modal
    :open="opened"
    :title="tr('detail')"
    width="960px"
    :close-on-backdrop="!busy"
    @update:open="!busy && (opened = $event)"
  >
    <div
      v-if="detail"
      class="review-detail"
      data-testid="novel-review-detail"
    >
      <p>{{ tr('snapshotNotice') }}</p>
      <h3>{{ detail.review.title }}</h3>
      <dl>
        <dt>{{ tr('submission') }}</dt><dd><code>{{ detail.review.id }}</code></dd>
        <dt>{{ tr('revision') }}</dt><dd>{{ detail.review.source_revision }}</dd>
        <dt>{{ tr('fingerprint') }}</dt><dd><code>{{ detail.review.snapshot_hash }}</code></dd>
        <dt>{{ tr('reviewState') }}</dt><dd>{{ tr(`status.${detail.review.status}`) }}</dd>
        <dt>{{ tr('preparation') }}</dt><dd>{{ tr(`preparationStatus.${detail.review.preparation_status}`) }}</dd>
      </dl>
      <img
        v-if="detail.review.cover_url"
        :src="detail.review.cover_url"
        :alt="tr('cover')"
        class="review-cover"
      >
      <p v-if="detail.review.reason">
        {{ detail.review.reason }}
      </p>
      <p
        v-if="detail.review.preparation_error"
        role="alert"
      >
        {{ tr('preparationFailed') }}
      </p>
      <div
        v-if="detail.review.status === 'pending'"
        class="review-actions"
      >
        <Button
          variant="secondary"
          :disabled="busy"
          @click="prepare()"
        >
          {{ tr('prepareText') }}
        </Button>
        <ConfirmAction
          :label="tr('prepareDrama')"
          :confirm-text="tr('dramaConfirm')"
          :cancel-label="tr('cancel')"
          :ok-label="tr('prepareDrama')"
          :action="prepareDrama"
        />
        <Button
          variant="ghost"
          :disabled="busy"
          @click="inspect(detail.review)"
        >
          {{ tr('refresh') }}
        </Button>
      </div>
      <details open>
        <summary>{{ tr('source') }}</summary><pre>{{ formatted(detail.content) }}</pre>
      </details>
      <details><summary>{{ tr('sourceConfig') }}</summary><pre>{{ formatted(detail.play_config) }}</pre></details>
      <details open>
        <summary>{{ tr(detail.review.preparation_status === 'ready' ? 'preparedConfig' : 'partialPreparedConfig') }}</summary><pre>{{ formatted(detail.prepared_play_config) }}</pre>
      </details>
      <details><summary>{{ tr('assets') }}</summary><pre>{{ formatted(detail.assets) }}</pre></details>
      <FormField
        v-if="detail.review.status === 'pending'"
        :label="tr('reason')"
        :hint="tr('reasonHint')"
      >
        <Textarea
          v-model="reason"
          :rows="3"
          :maxlength="2000"
          :disabled="busy"
          data-testid="novel-review-reason"
        />
      </FormField>
      <p
        v-if="error"
        class="review-error"
        role="alert"
      >
        {{ error }}
      </p>
    </div>
    <template #footer>
      <Button
        variant="ghost"
        :disabled="busy"
        @click="opened = false"
      >
        {{ tr('close') }}
      </Button>
      <template v-if="detail?.review.status === 'pending'">
        <Button
          variant="secondary"
          :disabled="busy || !reason.trim()"
          data-testid="novel-review-reject"
          @click="decide('rejected').catch(() => {})"
        >
          {{ tr('reject') }}
        </Button>
        <ConfirmAction
          v-if="detail.review.preparation_status === 'ready' && !busy"
          :label="tr('approve')"
          :confirm-text="tr('approveConfirm')"
          :cancel-label="tr('cancel')"
          :ok-label="tr('approve')"
          :action="() => decide('approved')"
          data-testid="novel-review-approve"
        />
      </template>
      <ConfirmAction
        v-if="detail?.review.status === 'approved' && !busy"
        :label="tr('publish')"
        :confirm-text="tr('publishConfirm')"
        :cancel-label="tr('cancel')"
        :ok-label="tr('publish')"
        :action="publish"
        data-testid="novel-review-publish"
      />
    </template>
  </Modal>
</template>

<style scoped>
.review-detail { display: grid; gap: var(--drama-space-md); }
.review-detail dl { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: var(--drama-space-sm); }
.review-detail dd { margin: 0; overflow-wrap: anywhere; }
.review-detail pre { white-space: pre-wrap; overflow-wrap: anywhere; padding: var(--drama-space-md); background: var(--drama-color-surface); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); max-height: 360px; overflow-y: auto; }
.review-cover { width: 160px; max-height: 240px; object-fit: contain; }
.review-actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.review-error { color: var(--drama-color-danger); }
</style>
